/**
 * Dashboard route registration — composition shell wires URL search to
 * DashboardRoute (plan §3.4: router.tsx is not edited in 5/6/7).
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';

import {
  DashboardSearchParamsSchema,
  type DashboardSearchParams,
  type SectorId,
  type SortState,
  type Ticker,
} from '@meridian/contracts';

import { Skeleton } from '@/components/primitives/skeleton';
import { rootRoute } from '@/router-root';

const loadDashboardRoute = () => import('./dashboard-route');

const DashboardScreen = lazy(async () => {
  const module = await loadDashboardRoute();
  return { default: module.DashboardRoute };
});

/**
 * Pure search patch for sortA / sortB URL writes (unit-testable).
 *
 * Typed against `DashboardSearchParams` rather than `Record<string, unknown>`:
 * the erased form is what forced a `@ts-expect-error` at both call sites, and a
 * suppression on the URL write path outlives its cause — rename `sortA` in the
 * schema and this would still compile, with Zod's `.catch()` quietly swallowing
 * the unknown field into the default sort on read.
 */
export function patchSortSearch(
  field: 'sortA' | 'sortB',
  sort: SortState,
): (prev: Partial<DashboardSearchParams>) => DashboardSearchParams {
  /* The router hands `prev` as a PARTIAL, so the patch is re-parsed rather than
     spread-and-asserted: every field carries a `.default()`/`.catch()`, making
     the result total by construction and the write path validated at the same
     boundary the read path is. */
  return (prev) =>
    DashboardSearchParamsSchema.parse(
      field === 'sortA' ? { ...prev, sortA: sort } : { ...prev, sortB: sort },
    );
}

/** Sector detail navigation target — preserves win/idx. */
export function sectorDetailTarget(
  sectorId: SectorId,
  search: Pick<DashboardSearchParams, 'win' | 'idx'>,
): {
  readonly to: '/sector/$sectorId';
  readonly params: { readonly sectorId: SectorId };
  readonly search: {
    readonly win: DashboardSearchParams['win'];
    readonly idx: DashboardSearchParams['idx'];
  };
} {
  return {
    to: '/sector/$sectorId',
    params: { sectorId },
    search: { win: search.win, idx: search.idx },
  };
}

/** Stock detail navigation target — preserves win/idx. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<DashboardSearchParams, 'win' | 'idx'>,
): {
  readonly to: '/stock/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: {
    readonly win: DashboardSearchParams['win'];
    readonly idx: DashboardSearchParams['idx'];
  };
} {
  return {
    to: '/stock/$ticker',
    params: { ticker },
    search: { win: search.win, idx: search.idx },
  };
}

/**
 * One writer, called twice — the two copies differed only by the field name.
 *
 * `dashboardRoute.useNavigate()`, not the bare `useNavigate()`: unscoped, the
 * router cannot know this route's search schema and narrows a search updater to
 * `never`, which is what the two `@ts-expect-error` suppressions were hiding.
 */
function useSearchSortWriter(field: 'sortA' | 'sortB'): (sort: SortState) => void {
  const navigate = dashboardRoute.useNavigate();
  return useCallback(
    (sort: SortState) => {
      void navigate({ search: patchSortSearch(field, sort), replace: true });
    },
    [navigate, field],
  );
}

function useDashboardSortCallbacks() {
  return {
    onSortAChange: useSearchSortWriter('sortA'),
    onSortBChange: useSearchSortWriter('sortB'),
  };
}

function useDashboardDetailNavigation(search: DashboardSearchParams) {
  const navigate = useNavigate();
  const { win, idx } = search;
  const onSectorSelect = useCallback(
    (sectorId: SectorId) => {
      void navigate(sectorDetailTarget(sectorId, { win, idx }));
    },
    [navigate, win, idx],
  );

  const onTickerSelect = useCallback(
    (ticker: Ticker) => {
      void navigate(stockDetailTarget(ticker, { win, idx }));
    },
    [navigate, win, idx],
  );
  return { onSectorSelect, onTickerSelect };
}

function DashboardRouteAdapter(): JSX.Element {
  const search = dashboardRoute.useSearch();
  const { onSortAChange, onSortBChange } = useDashboardSortCallbacks();
  const { onSectorSelect, onTickerSelect } = useDashboardDetailNavigation(search);

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading dashboard" />}>
      <DashboardScreen
        window={search.win}
        index={search.idx}
        sortA={search.sortA}
        sortB={search.sortB}
        onSortAChange={onSortAChange}
        onSortBChange={onSortBChange}
        onSectorSelect={onSectorSelect}
        onTickerSelect={onTickerSelect}
      />
    </Suspense>
  );
}

export const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>) => DashboardSearchParamsSchema.parse(search),
  loader: loadDashboardRoute,
  component: DashboardRouteAdapter,
});
